import useUserData from 'hooks/useUserData';
import { useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import {
  isSuggestionAssignedToMe,
  isSuggestionAssignedToOthers,
  isSuggestionClosed,
} from '../helpers';
import { useMyHubQuery, useUnassignedSuggestionsQuery } from '../queries';
import { ArticleSourceType } from '@buildwise/pages/knowledge/hooks/useArticlesQuery';

function useMySuggestions() {
  const { user, companyId, isNovaAdmin } = useUserData();
  const { hash } = useLocation();
  const {
    tasks: suggestions,
    isLoading,
    isIdle,
    refetch,
  } = useMyHubQuery({
    companyId,
    username: user?.username || '',
    articleType: ArticleSourceType.SUGGESTION,
  });
  const { suggestions: unassignedSuggestions } = useUnassignedSuggestionsQuery({
    companyId,
    shouldFetch: isNovaAdmin,
  });
  const [tab, setTab] = useState<string>(
    decodeURIComponent(hash).replace('#', '') || 'assigned-to-me',
  );
  const countTasks = useMemo(() => {
    let totaltasks: Record<string, number> = {
      unassigned: unassignedSuggestions.length,
    };
    if (user) {
      suggestions.forEach((suggestion) => {
        if (isSuggestionAssignedToMe({ suggestion, user })) {
          totaltasks['assigned-to-me'] = totaltasks['assigned-to-me']
            ? totaltasks['assigned-to-me'] + 1
            : 1;
        } else if (isSuggestionAssignedToOthers({ suggestion, user })) {
          totaltasks['assigned-to-others'] = totaltasks['assigned-to-others']
            ? totaltasks['assigned-to-others'] + 1
            : 1;
        }
      });
    }

    return totaltasks;
  }, [suggestions, user, unassignedSuggestions]);
  const tasksData = useMemo(() => {
    let tasksData = [...suggestions];

    if (user) {
      switch (tab) {
        case 'assigned-to-me': {
          tasksData = suggestions.filter((suggestion) =>
            isSuggestionAssignedToMe({ suggestion, user }),
          );
          break;
        }

        case 'assigned-to-others': {
          tasksData = suggestions.filter((suggestion) =>
            isSuggestionAssignedToOthers({ suggestion, user }),
          );
          break;
        }

        case 'closed': {
          tasksData = suggestions.filter((suggestion) => isSuggestionClosed({ suggestion, user }));
          break;
        }

        case 'unassigned': {
          tasksData = [...unassignedSuggestions];
          break;
        }

        default: {
          tasksData = [...suggestions];
        }
      }
    }

    return tasksData;
  }, [suggestions, tab, unassignedSuggestions]);

  return {
    countTasks,
    isIdle,
    isLoading,
    tab,
    tasksData,
    refetch,
    setTab,
  };
}

export default useMySuggestions;
